import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock, Sparkles } from "lucide-react";

const fmtEuro = (n) => "€ " + (Number(n) || 0).toFixed(2).replace(".", ",");
const fmtDate = (s) => (s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const STATUS_META = {
  in_review: { label: "In valutazione", tone: "amber", icon: Clock, desc: "Casa Madre sta valutando la tua richiesta. Riceverai una quotazione entro 24-48h." },
  quotata: { label: "Quotata", tone: "emerald", icon: CheckCircle2, desc: "Quotazione ufficiale ricevuta. Se ti va bene, conferma la prenotazione." },
  rifiutata: { label: "Rifiutata", tone: "red", icon: XCircle, desc: "Casa Madre ha rifiutato — vedi motivazione." },
  confermata: { label: "Confermata", tone: "blue", icon: CheckCircle2, desc: "Richiesta confermata: è ora una prenotazione ufficiale." },
};
const TONE = {
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  red: "bg-red-50 border-red-200 text-red-900",
  blue: "bg-blue-50 border-blue-200 text-blue-900",
};

/**
 * Page: "Le mie richieste" per cliente loggato.
 * Prop opzionale `embedded=true` per usarlo come tab dentro un'area account.
 */
export default function AccountRequests({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/config-requests/mine");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Errore caricamento: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const confirm = async (req) => {
    setConfirming(req.id);
    try {
      const r = await api.put(`/config-requests/${req.id}/confirm`);
      toast.success("Prenotazione confermata!");
      load();
      if (r.data?.prenotazione_id) {
        // Se esiste una pagina prenotazione dedicata, si può ridirigere qui.
      }
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setConfirming(null);
    }
  };

  const inner = (
    <div className="space-y-4" data-testid="my-requests">
      {!embedded && (
        <div>
          <h1 className="font-heading font-bold text-3xl mb-1">Le mie richieste</h1>
          <p className="text-sm text-muted-foreground">Configurazioni personalizzate che hai inviato alle Casa Madre.</p>
        </div>
      )}
      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground" data-testid="empty-my-requests">
          Non hai ancora inviato nessuna richiesta di configurazione.
        </div>
      ) : (
        rows.map((r) => {
          const s = STATUS_META[r.status] || STATUS_META.in_review;
          const Icon = s.icon;
          return (
            <div key={r.id} className={`border rounded-sm p-4 ${TONE[s.tone]}`} data-testid={`my-req-${r.id}`}>
              <div className="grid lg:grid-cols-12 gap-3">
                <div className="lg:col-span-4">
                  <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider mb-1">
                    <Icon className="w-3 h-3" /> {s.label}
                  </div>
                  <div className="font-heading font-bold text-lg">{r.product_name}</div>
                  <div className="text-[11px] font-mono opacity-70">{r.sku_madre}</div>
                  <div className="text-[10px] opacity-60 mt-1">Inviata {fmtDate(r.created_at)}</div>
                </div>

                <div className="lg:col-span-4 text-xs">
                  <div className="overline opacity-70 mb-1">Configurazione</div>
                  <ul className="space-y-0.5">
                    {Object.entries(r.combination || {}).map(([k, v]) => (
                      <li key={k}><span className="opacity-70">{k}:</span> <strong>{v.label || v.code || String(v)}</strong></li>
                    ))}
                  </ul>
                </div>

                <div className="lg:col-span-4 flex flex-col gap-2 items-end">
                  <div className="text-right w-full">
                    {r.status === "quotata" || r.status === "confermata" ? (
                      <>
                        <div className="text-[10px] uppercase tracking-wider opacity-70">Prezzo confermato</div>
                        <div className="font-heading font-bold text-2xl">{fmtEuro(r.prezzo_quotato)}</div>
                        {r.lead_time_gg && <div className="text-[11px] opacity-70">consegna in {r.lead_time_gg} giorni</div>}
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] uppercase tracking-wider opacity-70">Prezzo indicativo</div>
                        <div className="font-heading text-lg">{fmtEuro(r.prezzo_indicativo)}</div>
                      </>
                    )}
                  </div>

                  {r.status === "quotata" && (
                    <button onClick={() => confirm(r)} disabled={confirming === r.id}
                      data-testid={`confirm-${r.id}`}
                      className="w-full h-10 bg-emerald-600 text-white rounded-sm text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-emerald-700 disabled:opacity-50">
                      {confirming === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Conferma prenotazione
                    </button>
                  )}
                </div>
              </div>

              {(r.note_casa_madre || r.reject_reason) && (
                <div className="mt-3 pt-3 border-t border-black/10 text-xs italic opacity-80">
                  {r.reject_reason && <><strong>Motivo rifiuto:</strong> {r.reject_reason}</>}
                  {r.note_casa_madre && <><strong>Nota Casa Madre:</strong> {r.note_casa_madre}</>}
                </div>
              )}

              <div className="mt-2 text-[10px] opacity-60">{s.desc}</div>
            </div>
          );
        })
      )}
    </div>
  );

  if (embedded) return inner;
  return (
    <div className="max-w-5xl mx-auto px-6 py-6">
      {inner}
    </div>
  );
}
